// -----------------------------------------------------------------------------
//  Property Galleries — SINGLE SOURCE OF TRUTH used by BOTH /contact and
//  /booking card carousels. Guarantees exactly 8 photos per hotel/bivouac/
//  refuge, in the same order, so the two pages always render an identical
//  visual experience.
//
//  Sourcing precedence (deduped, first-wins):
//    0. CMS-managed `card` section (Hotel card images in /admin/galleries)
//       — same slot used by the homepage hotel card, so editing ONCE keeps
//       every card surface (Home, /contact, /booking, etc.) in sync.
//    1. `hotel.image`             — the marquee shot from `data.js`
//    2. `hotel.gallery[]`         — curated shots from `data.js`
//    3. `hotelDetails.rooms[].image` — room-level photography from
//        `hotelDetails.js`.
//    4. THEMED_FALLBACKS[category] — category-appropriate padding so every
//        card renders a full 8-slide carousel, even for smaller listings.
// -----------------------------------------------------------------------------

import { useEffect, useState } from "react";
import { HOTELS } from "@/lib/data";
import { getHotelDetails } from "@/lib/hotelDetails";

const API = process.env.REACT_APP_BACKEND_URL || "";

export const GALLERY_TARGET_COUNT = 8;

// Category-tuned fallback pools — reused Unsplash/Pexels URLs already
// present elsewhere in the codebase.
const THEMED_FALLBACKS = {
  hotel: [
    "https://images.unsplash.com/photo-1539020140153-e479b8c22e70?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
    "https://images.unsplash.com/photo-1566665797739-1674de7a421a?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1611892440504-42a792e24d32?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1591088398332-8a7791972843?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1618773928121-c32242e63f39?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?auto=format&fit=crop&w=1600&q=80",
  ],
  refuge: [
    "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1519681393784-d120267933ba?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1517824806704-9040b037703b?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1454391304352-2bf4678b1a7a?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1499856871958-5b9627545d1a?auto=format&fit=crop&w=1600&q=80",
  ],
  bivouac: [
    "https://images.unsplash.com/photo-1509316785289-025f5b846b35?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1489493887464-892be6d1daae?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1518791841217-8f162f1e1131?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1509316975850-ff9c5deb0cd9?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1533106418989-88406c7cc8ca?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1554979242-8b3f8f7f9d1e?auto=format&fit=crop&w=1600&q=80",
    "https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1600&q=80",
  ],
};

const dedupe = (arr) => {
  const seen = new Set();
  const out = [];
  for (const item of arr) {
    if (!item || typeof item !== "string") continue;
    const key = item.trim();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
};

// Convert relative /api/... URLs served by our CMS to absolute origins
// so they load correctly in preview and production alike.
const resolveUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  if (url.startsWith("/api/")) return `${API}${url}`;
  return url;
};

// Build the auto-derived base list (no CMS input). Kept pure + memoised so
// consumers get a stable reference across re-renders.
const buildAutoDerived = (hotel) => {
  const details = getHotelDetails(hotel.slug);
  const heroImg = hotel.image ? [hotel.image] : [];
  const dataGallery = Array.isArray(hotel.gallery) ? hotel.gallery : [];
  const roomImgs = Array.isArray(details?.rooms)
    ? details.rooms.map((r) => r?.image).filter(Boolean)
    : [];

  const combined = dedupe([...heroImg, ...dataGallery, ...roomImgs]);

  if (combined.length >= GALLERY_TARGET_COUNT) {
    return combined.slice(0, GALLERY_TARGET_COUNT);
  }

  const pool = THEMED_FALLBACKS[hotel.category] || THEMED_FALLBACKS.hotel;
  const padded = [...combined];
  for (const candidate of pool) {
    if (padded.length >= GALLERY_TARGET_COUNT) break;
    if (!padded.includes(candidate)) padded.push(candidate);
  }
  while (padded.length < GALLERY_TARGET_COUNT) {
    padded.push(padded[padded.length - 1] || heroImg[0] || pool[0]);
  }
  return padded.slice(0, GALLERY_TARGET_COUNT);
};

const AUTO_CACHE = new Map();
const getAutoDerived = (slug) => {
  if (AUTO_CACHE.has(slug)) return AUTO_CACHE.get(slug);
  const hotel = HOTELS.find((h) => h.slug === slug);
  const arr = hotel ? buildAutoDerived(hotel) : THEMED_FALLBACKS.hotel.slice(0, GALLERY_TARGET_COUNT);
  AUTO_CACHE.set(slug, arr);
  return arr;
};

/**
 * Synchronous accessor kept for tests / non-React contexts. Returns the
 * auto-derived pool WITHOUT CMS overrides.
 * @deprecated Prefer `usePropertyGallery(slug)` inside React components so
 * the CMS override is honoured live.
 */
export const getPropertyGallery = (slug) => getAutoDerived(slug);

// ─────────────────────────────────────────────────────────────────────────
// Module-level cache of the CMS fetch — one in-flight promise per hotel so
// two <Card slug="X" /> instances never trigger two identical HTTPs. The
// resolved value is reused for the entire session; the admin cache-buster
// (query string) will force a fresh fetch after edits.
// ─────────────────────────────────────────────────────────────────────────
const CMS_CACHE = new Map(); // slug → { images: string[]|null, ts: number }
const CMS_IN_FLIGHT = new Map(); // slug → Promise

const fetchCmsCardImages = (slug) => {
  if (CMS_CACHE.has(slug)) {
    return Promise.resolve(CMS_CACHE.get(slug).images);
  }
  if (CMS_IN_FLIGHT.has(slug)) {
    return CMS_IN_FLIGHT.get(slug);
  }
  const url = `${API}/api/property-galleries/by-hotel/${slug}`;
  const p = fetch(url)
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((data) => {
      const sections = data?.sections || {};
      // Prefer explicit "gallery" images. Also honour "hero" (single image)
      // when the operator only uploaded a hero — it becomes slide 0.
      const heroImgs = sections?.card?.hero?.images || [];
      const galleryImgs = sections?.card?.gallery?.images || [];
      const urls = dedupe(
        [
          ...heroImgs.map((i) => resolveUrl(i.url)),
          ...galleryImgs.map((i) => resolveUrl(i.url)),
        ].filter(Boolean)
      );
      CMS_CACHE.set(slug, { images: urls.length ? urls : null, ts: Date.now() });
      CMS_IN_FLIGHT.delete(slug);
      return urls.length ? urls : null;
    })
    .catch(() => {
      // Silent — CMS overrides are optional. Fall back to auto-derived.
      CMS_CACHE.set(slug, { images: null, ts: Date.now() });
      CMS_IN_FLIGHT.delete(slug);
      return null;
    });
  CMS_IN_FLIGHT.set(slug, p);
  return p;
};

/**
 * React hook — returns an ORDERED array of exactly 8 image URLs for the
 * given slug, honouring CMS overrides from /admin/galleries → "Hotel card
 * images" section. Falls back to the auto-derived pool while the fetch is
 * in flight or when the CMS has no images stored for the slug.
 *
 * The returned reference is stable across identical states so React can
 * skip re-renders of unchanged siblings.
 */
export const usePropertyGallery = (slug) => {
  const initial = getAutoDerived(slug);
  const [gallery, setGallery] = useState(initial);

  useEffect(() => {
    if (!slug) return undefined;
    let cancelled = false;
    // Immediately reset to the auto-derived pool for the new slug so the
    // returned array always matches the requested hotel — critical when a
    // parent re-renders with a different `slug`.
    setGallery(getAutoDerived(slug));

    fetchCmsCardImages(slug).then((cmsUrls) => {
      if (cancelled) return;
      if (!cmsUrls || cmsUrls.length === 0) {
        // Keep the auto-derived pool.
        setGallery(getAutoDerived(slug));
        return;
      }
      // Merge: CMS images first, pad from auto-derived pool if needed.
      const auto = getAutoDerived(slug);
      const merged = dedupe([...cmsUrls, ...auto]).slice(0, GALLERY_TARGET_COUNT);
      // Pad final gaps (defensive) so consumers still get exactly 8.
      while (merged.length < GALLERY_TARGET_COUNT) {
        merged.push(merged[merged.length - 1] || auto[0]);
      }
      setGallery(merged);
    });

    return () => { cancelled = true; };
  }, [slug]);

  return gallery;
};

export default getPropertyGallery;
